import React, { useEffect, useMemo, useState } from 'react';
import { CalendarIcon, ClockIcon, MapPinIcon, MonitorIcon, MoreVerticalIcon, FilterIcon, ChevronDownIcon, LoaderIcon } from 'lucide-react';
import { collection, collectionGroup, query, where, getDocs, getDoc, doc, orderBy, Timestamp, updateDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../providers/ThemeProvider';
import { StudentLessonDetailModal } from '../modals/StudentLessonDetailModal';
import { StudentCancelLessonModal } from '../modals/StudentCancelLessonModal';
import toast from 'react-hot-toast';
type Lesson = {
  id: string;
  teacherId: string;
  title: string;
  teacherName: string;
  instrument: string;
  startAt: Date;
  durationMinutes: number;
  location: string;
  status: string;
  notes: string;
  format: string;
  meetingLink?: string;
};
type FilterOption = 'All' | 'Upcoming' | 'Past';
export const StudentSchedule = () => {
  const {
    currentUser
  } = useAuth();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const [isLoading, setIsLoading] = useState(true);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [filter, setFilter] = useState<FilterOption>('All');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(null);
  const [lessonToCancel, setLessonToCancel] = useState<Lesson | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  useEffect(() => {
    if (currentUser) {
      fetchLessons();
    }
  }, [currentUser]);
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenMenuId(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);
  const fetchLessons = async () => {
    try {
      setIsLoading(true);
      if (!currentUser) return;
      // Step 1: Find teacher-student links (same as dashboard)
      const studentsQuery = query(collectionGroup(db, 'students'), where('linkedUid', '==', currentUser.uid));
      const studentsSnap = await getDocs(studentsQuery);
      if (studentsSnap.empty) {
        console.log('[MySchedule] No student-teacher links found for uid:', currentUser.uid);
        setLessons([]);
        setIsLoading(false);
        return;
      }
      // Build student-teacher pairs
      const studentTeacherPairs: {
        studentId: string;
        teacherId: string;
      }[] = [];
      studentsSnap.docs.forEach(docSnap => {
        const pathSegments = docSnap.ref.path.split('/');
        const teacherId = pathSegments[1];
        const studentData = docSnap.data();
        const studentIdForLessons = studentData.studentId || docSnap.id;
        studentTeacherPairs.push({
          studentId: studentIdForLessons,
          teacherId
        });
      });
      console.log('[MySchedule] Found student-teacher pairs:', studentTeacherPairs.length);
      // Step 2: Query ALL lessons for each student-teacher pair (past + future)
      // IMPORTANT: Must use 'asc' to match the existing Firestore composite index
      // (studentId ASC + startAt ASC). 'desc' would require a separate index.
      // We sort in JS after fetching.
      const lessonPromises = studentTeacherPairs.map(pair => {
        const calendarQuery = query(collectionGroup(db, 'calendar'), where('studentId', '==', pair.studentId), orderBy('startAt', 'asc'));
        return getDocs(calendarQuery).then(snap => {
          const lessons: any[] = [];
          snap.docs.forEach(lessonDoc => {
            const data = lessonDoc.data();
            if (data.status !== 'CANCELED' && data.cancelled !== true) {
              lessons.push({
                id: lessonDoc.id,
                teacherId: pair.teacherId,
                ...data
              });
            }
          });
          return lessons;
        });
      });
      const lessonResults = await Promise.all(lessonPromises);
      const allRawLessons = lessonResults.flat();
      console.log('[MySchedule] Total raw lessons fetched:', allRawLessons.length);
      // Step 3: Resolve teacher names
      const teacherIds = [...new Set(allRawLessons.map(l => l.teacherId))];
      const teacherNameMap: Record<string, string> = {};
      await Promise.all(teacherIds.map(async tid => {
        try {
          const profileDoc = await getDoc(doc(db, 'publicTeacherProfiles', tid));
          if (profileDoc.exists()) {
            const p = profileDoc.data();
            teacherNameMap[tid] = p.fullName || `${p.firstName || ''} ${p.lastName || ''}`.trim() || 'Teacher';
          } else {
            teacherNameMap[tid] = 'Teacher';
          }
        } catch {
          teacherNameMap[tid] = 'Teacher';
        }
      }));
      // Step 4: Format lessons
      const formattedLessons: Lesson[] = allRawLessons.map(raw => {
        const startDate = raw.startAt?.toDate ? raw.startAt.toDate() : new Date(raw.startAt);
        const locationStr = raw.location || '';
        const isOnline = locationStr.toLowerCase().includes('online') || locationStr.toLowerCase().includes('virtual') || locationStr.toLowerCase().includes('zoom') || locationStr.toLowerCase().includes('meet') || !!raw.meetingLink;
        return {
          id: raw.id,
          teacherId: raw.teacherId,
          title: raw.title || `${raw.instrument || 'Music'} Lesson`,
          teacherName: teacherNameMap[raw.teacherId] || 'Teacher',
          instrument: raw.instrument || 'Music',
          startAt: startDate,
          durationMinutes: raw.durationMinutes || 60,
          location: locationStr || (isOnline ? 'Online' : 'In-person'),
          status: (raw.status || 'SCHEDULED').toUpperCase(),
          notes: raw.notes || '',
          format: isOnline ? 'Online' : 'In-person',
          meetingLink: raw.meetingLink
        };
      });
      console.log('[MySchedule] Formatted lessons:', formattedLessons.length);
      setLessons(formattedLessons);
    } catch (error) {
      console.error('[MySchedule] Error fetching lessons:', error);
    } finally {
      setIsLoading(false);
    }
  };
  const handleCancelLesson = async () => {
    if (!lessonToCancel) return;
    try {
      const lessonRef = doc(db, 'teachers', lessonToCancel.teacherId, 'calendar', lessonToCancel.id);
      await updateDoc(lessonRef, {
        status: 'CANCELED',
        canceledBy: 'student',
        canceledAt: Timestamp.now()
      });
      // Create in-app alert for the teacher (non-blocking)
      try {
        const alertsRef = collection(db, 'teachers', lessonToCancel.teacherId, 'alerts');
        const studentName = currentUser?.displayName || 'A student';
        await addDoc(alertsRef, {
          type: 'lesson_canceled',
          message: `${studentName} canceled a lesson`,
          studentName,
          lessonTime: lessonToCancel.startAt,
          lessonId: lessonToCancel.id,
          reason: 'Lesson canceled by student',
          status: 'active',
          read: false,
          createdAt: serverTimestamp()
        });
      } catch (alertError) {
        console.warn('[MySchedule] Failed to create teacher alert (non-blocking):', alertError);
      }
      // Remove from local state
      setLessons(prev => prev.filter(l => l.id !== lessonToCancel.id));
      setLessonToCancel(null);
      toast.success('Lesson canceled successfully');
    } catch (error) {
      console.error('Error canceling lesson:', error);
      toast.error('Failed to cancel lesson. Please try again.');
    }
  };
  const {
    todayLessons,
    upcomingLessons,
    pastLessons
  } = useMemo(() => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrowStart = new Date(todayStart);
    tomorrowStart.setDate(tomorrowStart.getDate() + 1);
    const todayList: Lesson[] = [];
    const upcomingList: Lesson[] = [];
    const pastList: Lesson[] = [];
    lessons.forEach(lesson => {
      const d = lesson.startAt;
      if (lesson.status === 'COMPLETED' || d < todayStart) {
        pastList.push(lesson);
      } else if (d >= todayStart && d < tomorrowStart) {
        todayList.push(lesson);
      } else {
        upcomingList.push(lesson);
      }
    });
    todayList.sort((a, b) => a.startAt.getTime() - b.startAt.getTime());
    upcomingList.sort((a, b) => a.startAt.getTime() - b.startAt.getTime());
    pastList.sort((a, b) => b.startAt.getTime() - a.startAt.getTime());
    console.log('[MySchedule] Today:', todayList.length, 'Upcoming:', upcomingList.length, 'Past:', pastList.length);
    return {
      todayLessons: todayList,
      upcomingLessons: upcomingList,
      pastLessons: pastList
    };
  }, [lessons]);
  const todayDateStr = new Date().toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
  const formatDateLabel = (d: Date) => {
    return {
      monthDay: d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      })
    };
  };
  const formatTime = (d: Date) => {
    const timeStr = d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
    const match = timeStr.match(/(\d+:\d+)\s*(AM|PM)/i);
    if (!match) return {
      time: timeStr,
      period: ''
    };
    return {
      time: match[1],
      period: match[2].toUpperCase()
    };
  };
  const getStatusTag = (status: string, isPast: boolean) => {
    if (isPast || status === 'COMPLETED') {
      return <span className={`px-3 py-1 rounded-full text-xs font-medium ${isDark ? 'bg-green-900/30 text-green-400 border border-green-700' : 'bg-green-50 text-green-700 border border-green-200'}`} data-id="element-8029">
          Completed
        </span>;
    }
    if (status === 'SCHEDULED' || status === 'CONFIRMED') {
      return <span className={`px-3 py-1 rounded-full text-xs font-medium ${isDark ? 'bg-green-900/30 text-green-400 border border-green-700' : 'bg-green-50 text-green-700 border border-green-200'}`} data-id="element-8030">
          Confirmed
        </span>;
    }
    return <span className={`px-3 py-1 rounded-full text-xs font-medium ${isDark ? 'bg-blue-900/30 text-blue-400 border border-blue-700' : 'bg-blue-50 text-blue-700 border border-blue-200'}`} data-id="element-8031">
        Scheduled
      </span>;
  };
  const isOnlineFn = (lesson: Lesson) => lesson.format?.toLowerCase() === 'online';
  const LessonCard = ({
    lesson,
    isToday = false,
    isPast = false
  }: {
    lesson: Lesson;
    isToday?: boolean;
    isPast?: boolean;
  }) => {
    const {
      monthDay
    } = formatDateLabel(lesson.startAt);
    const {
      time,
      period
    } = formatTime(lesson.startAt);
    const online = isOnlineFn(lesson);
    return <div onClick={() => setSelectedLesson(lesson)} className={`flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 p-4 sm:p-5 rounded-xl cursor-pointer transition-all duration-200 ease-out ${isDark ? 'bg-navy-800 border border-navy-800 hover:-translate-y-0.5 hover:bg-[#1e2a4a] hover:shadow-lg hover:shadow-blue-500/10' : 'bg-white border border-gray-100 shadow-sm shadow-gray-100/50 hover:-translate-y-0.5 hover:shadow-md hover:shadow-gray-200/60'}`} data-id="element-8032">
        {/* Date/Time Block */}
        <div className={`flex-shrink-0 w-20 h-20 rounded-xl flex flex-col items-center justify-center ${isToday ? isDark ? 'bg-gradient-to-br from-blue-900/40 to-blue-800/30' : 'bg-gradient-to-br from-blue-50 to-blue-100/80' : isPast ? isDark ? 'bg-navy-900/40' : 'bg-gray-50' : isDark ? 'bg-gradient-to-br from-navy-900/60 to-blue-900/20' : 'bg-gradient-to-br from-blue-50/80 to-slate-100/60'}`} data-id="element-8033">
          {isToday ? <>
              <span className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-8034">
                {time}
              </span>
              <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8035">
                {period}
              </span>
            </> : <>
              <span className={`text-sm font-bold ${isDark ? 'text-gray-200' : 'text-gray-900'}`} data-id="element-8036">
                {monthDay}
              </span>
              <span className={`text-xs font-medium mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8037">
                {time} {period}
              </span>
            </>}
        </div>

        {/* Center Content */}
        <div className="flex-1 min-w-0" data-id="element-8038">
          <h4 className={`text-base font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-8039">
            {lesson.title}
          </h4>
          <p className={`text-sm mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8040">
            with {lesson.teacherName}
          </p>
          <div className={`flex items-center gap-3 mt-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8041">
            <span className="flex items-center gap-1" data-id="element-8042">
              {online ? <MonitorIcon className="h-3.5 w-3.5" data-id="element-8043" /> : <MapPinIcon className="h-3.5 w-3.5" data-id="element-8044" />}
              {online ? 'Online' : 'In-person'}
            </span>
            <span className={isDark ? 'text-gray-600' : 'text-gray-300'} data-id="element-8045">
              ·
            </span>
            <span className="flex items-center gap-1" data-id="element-8046">
              <ClockIcon className="h-3.5 w-3.5" data-id="element-8047" />
              {lesson.durationMinutes} min
            </span>
          </div>
        </div>

        {/* Right: Status + Actions */}
        <div className="flex flex-row sm:flex-col items-center sm:items-end gap-3 sm:gap-2 flex-shrink-0 w-full sm:w-auto justify-between sm:justify-start" data-id="element-8048">
          <div className="flex items-center gap-2" data-id="element-8049">
            {getStatusTag(lesson.status, isPast)}
            {!isPast && lesson.status !== 'COMPLETED' && lesson.status !== 'CANCELED' && <div className="relative" data-id="element-8050">
                  <button onClick={e => {
              e.stopPropagation();
              setOpenMenuId(openMenuId === lesson.id ? null : lesson.id);
            }} className={`p-1 rounded-md transition-colors ${isDark ? 'hover:bg-navy-700 text-gray-400' : 'hover:bg-gray-100 text-gray-400'}`} data-id="element-8051">
                    <MoreVerticalIcon className="h-4 w-4" data-id="element-8052" />
                  </button>
                  {openMenuId === lesson.id && <div className={`absolute right-0 mt-1 w-36 rounded-lg border shadow-lg z-20 py-1 ${isDark ? 'bg-navy-800 border-navy-600' : 'bg-white border-gray-200'}`} onClick={e => e.stopPropagation()} data-id="element-8053">
                      <button onClick={() => {
                setOpenMenuId(null);
                setLessonToCancel(lesson);
              }} className={`w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors`} data-id="element-8054">
                        Cancel Lesson
                      </button>
                    </div>}
                </div>}
          </div>
          <button onClick={e => {
          e.stopPropagation();
          setSelectedLesson(lesson);
        }} className={`px-4 py-1.5 rounded-lg text-sm font-medium border transition-colors ${isDark ? 'border-navy-600 text-gray-300 hover:bg-navy-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-8055">
            {isPast ? 'View Notes' : 'View Details'}
          </button>
        </div>
      </div>;
  };
  const SectionHeader = ({
    title,
    count,
    badge
  }: {
    title: string;
    count?: number;
    badge?: string;
  }) => <div className="flex items-center gap-3 mb-4" data-id="element-8056">
      <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-8057">
        {title}
      </h3>
      {badge && <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${isDark ? 'bg-blue-900/40 text-blue-400' : 'bg-blue-100 text-blue-700'}`} data-id="element-8058">
          {badge}
        </span>}
      {count !== undefined && count > 0 && !badge && <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${isDark ? 'bg-blue-900/40 text-blue-400' : 'bg-blue-100 text-blue-700'}`} data-id="element-8059">
          {count}
        </span>}
    </div>;
  const EmptySection = ({
    message
  }: {
    message: string;
  }) => <div className={`text-center py-10 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-8060">
      <CalendarIcon className="h-8 w-8 mx-auto mb-2 opacity-30" data-id="element-8061" />
      <p className="text-sm" data-id="element-8062">{message}</p>
    </div>;
  const showToday = filter === 'All' || filter === 'Upcoming';
  const showUpcoming = filter === 'All' || filter === 'Upcoming';
  const showPast = filter === 'All' || filter === 'Past';
  return <div className="max-w-4xl" data-id="element-8063">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-8" data-id="element-8064">
        <div data-id="element-8065">
          <h1 className={`text-3xl sm:text-4xl font-extrabold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-8066">
            My Schedule
          </h1>
          <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8067">
            All your music lessons in one place
          </p>
        </div>

        {/* Filter Dropdown */}
        <div className="relative" data-id="element-8068">
          <button onClick={() => setIsFilterOpen(!isFilterOpen)} className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${isDark ? 'bg-navy-800 border-navy-600 text-gray-300 hover:bg-navy-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-8069">
            <FilterIcon className="h-4 w-4" data-id="element-8070" />
            Filter
            <ChevronDownIcon className="h-4 w-4" data-id="element-8071" />
          </button>
          {isFilterOpen && <>
              <div className="fixed inset-0 z-10" onClick={() => setIsFilterOpen(false)} data-id="element-8072" />
              <div className={`absolute right-0 mt-2 w-40 rounded-lg border shadow-lg z-20 py-1 ${isDark ? 'bg-navy-800 border-navy-600' : 'bg-white border-gray-200'}`} data-id="element-8073">
                {(['All', 'Upcoming', 'Past'] as FilterOption[]).map(option => <button key={option} onClick={() => {
              setFilter(option);
              setIsFilterOpen(false);
            }} className={`w-full text-left px-4 py-2 text-sm transition-colors ${filter === option ? isDark ? 'bg-navy-700 text-white' : 'bg-gray-100 text-gray-900' : isDark ? 'text-gray-300 hover:bg-navy-700' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-8074">
                      {option}
                    </button>)}
              </div>
            </>}
        </div>
      </div>

      {/* Loading Skeleton */}
      {isLoading && <div className="space-y-10" data-id="element-8075">
          {/* Today section skeleton */}
          <section data-id="element-8076">
            <div className="flex items-center gap-3 mb-4 animate-pulse" data-id="element-8077">
              <div className={`h-6 w-16 rounded ${isDark ? 'bg-navy-700' : 'bg-gray-200'}`} data-id="element-8078" />
              <div className={`h-5 w-20 rounded-full ${isDark ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-8079" />
            </div>
            <div className="space-y-3" data-id="element-8080">
              <div className={`flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 p-4 sm:p-5 rounded-xl ${isDark ? 'bg-navy-800 border border-navy-800' : 'bg-white border border-gray-100 shadow-sm shadow-gray-100/50'}`} data-id="element-8081">
                <div className="animate-pulse flex items-center gap-6 w-full" data-id="element-8082">
                  <div className={`flex-shrink-0 w-20 h-20 rounded-xl ${isDark ? 'bg-navy-700' : 'bg-gray-200'}`} data-id="element-8083" />
                  <div className="flex-1 min-w-0 space-y-2.5" data-id="element-8084">
                    <div className={`h-4 w-36 rounded ${isDark ? 'bg-navy-700' : 'bg-gray-200'}`} data-id="element-8085" />
                    <div className={`h-3 w-24 rounded ${isDark ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-8086" />
                    <div className="flex items-center gap-3" data-id="element-8087">
                      <div className={`h-3 w-16 rounded ${isDark ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-8088" />
                      <div className={`h-3 w-14 rounded ${isDark ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-8089" />
                    </div>
                  </div>
                  <div className="hidden sm:flex flex-col items-end gap-2" data-id="element-8090">
                    <div className={`h-6 w-20 rounded-full ${isDark ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-8091" />
                    <div className={`h-8 w-24 rounded-lg ${isDark ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-8092" />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Upcoming section skeleton */}
          <section data-id="element-8093">
            <div className="flex items-center gap-3 mb-4 animate-pulse" data-id="element-8094">
              <div className={`h-6 w-24 rounded ${isDark ? 'bg-navy-700' : 'bg-gray-200'}`} data-id="element-8095" />
              <div className={`h-5 w-8 rounded-full ${isDark ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-8096" />
            </div>
            <div className="space-y-3" data-id="element-8097">
              {[1, 2, 3].map(i => <div key={i} className={`flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 p-4 sm:p-5 rounded-xl ${isDark ? 'bg-navy-800 border border-navy-800' : 'bg-white border border-gray-100 shadow-sm shadow-gray-100/50'}`} data-id="element-8098">
                  <div className="animate-pulse flex items-center gap-6 w-full" data-id="element-8099">
                    <div className={`flex-shrink-0 w-20 h-20 rounded-xl ${isDark ? 'bg-navy-700' : 'bg-gray-200'}`} data-id="element-8100" />
                    <div className="flex-1 min-w-0 space-y-2.5" data-id="element-8101">
                      <div className={`h-4 rounded ${isDark ? 'bg-navy-700' : 'bg-gray-200'}`} style={{
                  width: `${100 + i * 20}px`
                }} data-id="element-8102" />
                      <div className={`h-3 w-24 rounded ${isDark ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-8103" />
                      <div className="flex items-center gap-3" data-id="element-8104">
                        <div className={`h-3 w-16 rounded ${isDark ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-8105" />
                        <div className={`h-3 w-14 rounded ${isDark ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-8106" />
                      </div>
                    </div>
                    <div className="hidden sm:flex flex-col items-end gap-2" data-id="element-8107">
                      <div className={`h-6 w-20 rounded-full ${isDark ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-8108" />
                      <div className={`h-8 w-24 rounded-lg ${isDark ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-8109" />
                    </div>
                  </div>
                </div>)}
            </div>
          </section>
        </div>}

      {!isLoading && <div className="space-y-10" data-id="element-8110">
          {/* Today Section */}
          {showToday && <section data-id="element-8111">
              <SectionHeader title="Today" badge={todayDateStr} data-id="element-8112" />
              {todayLessons.length > 0 ? <div className="space-y-3" data-id="element-8113">
                  {todayLessons.map(lesson => <LessonCard key={lesson.id} lesson={lesson} isToday data-id="element-8114" />)}
                </div> : <EmptySection message="No lessons scheduled for today" data-id="element-8115" />}
            </section>}

          {/* Upcoming Section */}
          {showUpcoming && <section data-id="element-8116">
              <SectionHeader title="Upcoming" count={upcomingLessons.length} data-id="element-8117" />
              {upcomingLessons.length > 0 ? <div className="space-y-3" data-id="element-8118">
                  {upcomingLessons.map(lesson => <LessonCard key={lesson.id} lesson={lesson} data-id="element-8119" />)}
                </div> : <EmptySection message="No upcoming lessons" data-id="element-8120" />}
            </section>}

          {/* Past Lessons Section */}
          {showPast && <section data-id="element-8121">
              <SectionHeader title="Past Lessons" count={pastLessons.length} data-id="element-8122" />
              {pastLessons.length > 0 ? <div className="space-y-3" data-id="element-8123">
                  {pastLessons.map(lesson => <LessonCard key={lesson.id} lesson={lesson} isPast data-id="element-8124" />)}
                </div> : <EmptySection message="No past lessons yet" data-id="element-8125" />}
            </section>}
        </div>}

      {/* Lesson Detail Modal */}
      <StudentLessonDetailModal isOpen={selectedLesson !== null} lesson={selectedLesson} onClose={() => setSelectedLesson(null)} isDark={isDark} data-id="element-8126" />

      {/* Cancel Lesson Modal */}
      <StudentCancelLessonModal isOpen={lessonToCancel !== null} onClose={() => setLessonToCancel(null)} onConfirm={handleCancelLesson} lessonTitle={lessonToCancel?.title} teacherName={lessonToCancel?.teacherName} isDark={isDark} data-id="element-8127" />
    </div>;
};